import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useParams } from 'react-router';

import AddIcon from '@mui/icons-material/Add';
import ClearIcon from '@mui/icons-material/Clear';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { blue, red } from '@mui/material/colors';
import { GridColDef, GridRenderCellParams, GridRenderEditCellParams, useGridApiRef } from '@mui/x-data-grid';

import Table from '~/components/v2/common/Table/Table';

import * as AccommodationService from '~/services/AccommodationService';

const NEW_ROW_CODE_PREFIX = 'new-';

interface PropertyFacilityRowData extends AccommodationService.AdminPropertyFacility {
  isNew?: boolean;
}

interface FacilityOption {
  code: string;
  name: string;
}

interface PendingChanges {
  facilityCode?: string;
  displayOrder?: number | null;
  featured?: boolean | null;
}

function NumberEditCell(props: GridRenderEditCellParams & { onChange: (value: number | undefined) => void }) {
  const { value, onChange } = props;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const numValue = event.target.value === '' ? undefined : Number(event.target.value);
    onChange(numValue);
  };

  return (
    <TextField size="small" type="number" value={value ?? ''} onChange={handleChange} fullWidth variant="outlined" />
  );
}

function FeaturedEditCell(props: GridRenderEditCellParams & { onChange: (value: boolean) => void }) {
  const { value, onChange } = props;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange(event.target.checked);
  };

  return <Checkbox checked={Boolean(value)} onChange={handleChange} />;
}

export default function AccommodationPropertyFacilitiesSection() {
  const { propertyId } = useParams<{ propertyId: string }>();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const apiRef = useGridApiRef();

  const [modifiedRows, setModifiedRows] = useState<Record<string, PendingChanges>>({});
  const [localRows, setLocalRows] = useState<Array<PropertyFacilityRowData>>([]);
  const [deletingCodes, setDeletingCodes] = useState<Set<string>>(new Set());

  // Refs to avoid stale closures in callbacks used by DataGrid
  const modifiedRowsRef = useRef<Record<string, PendingChanges>>({});

  useEffect(() => {
    modifiedRowsRef.current = modifiedRows;
  }, [modifiedRows]);

  // Fetch all facilities for the dropdown
  const { data: allFacilitiesData } = useQuery({
    queryKey: ['accommodation', 'facilities', 'all'],
    queryFn: async () => {
      const response = await AccommodationService.listFacilities({ limit: '1000', page: '1' });
      return response.result;
    },
  });

  const allFacilities = allFacilitiesData?.facilities ?? [];

  // Fetch property facilities
  const {
    data: propertyFacilitiesData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['accommodation', 'property-facilities', propertyId],
    queryFn: async () => {
      const response = await AccommodationService.listPropertyFacilities({ propertyId });
      return response.result;
    },
    enabled: !!propertyId,
  });

  const propertyFacilities = propertyFacilitiesData?.facilities ?? [];

  // Get facility options that are not already added to the property
  const availableFacilityOptions = useMemo(() => {
    const existingCodes = new Set(propertyFacilities.map((f) => f.code));
    const pendingNewCodes = new Set(
      localRows.map((r) => modifiedRowsRef.current[r.code]?.facilityCode).filter(Boolean),
    );
    return allFacilities
      .filter((f) => !existingCodes.has(f.code) && !pendingNewCodes.has(f.code))
      .map((f) => ({ code: f.code, name: f.name }));
  }, [allFacilities, propertyFacilities, localRows, modifiedRowsRef]);

  // Add property facility mutation
  const addMutation = useMutation({
    mutationFn: async (data: AccommodationService.PropertyFacilityAddPayload & { tempCode: string }) => {
      const { tempCode, ...payload } = data;
      return await AccommodationService.addPropertyFacility({ propertyId }, payload);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['accommodation', 'property-facilities', propertyId] });
      setLocalRows((prev) => prev.filter((r) => r.code !== variables.tempCode));
      setModifiedRows((prev) => {
        const updated = { ...prev };
        delete updated[variables.tempCode];
        return updated;
      });
      enqueueSnackbar('Facility added to property successfully', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(`Error adding facility: ${error.message}`, { variant: 'error' });
    },
  });

  // Update property facility mutation
  const updateMutation = useMutation({
    mutationFn: async ({
      facilityCode,
      data,
    }: {
      facilityCode: string;
      data: AccommodationService.PropertyFacilityUpdatePayload;
    }) => {
      return await AccommodationService.updatePropertyFacility({ propertyId, facilityCode }, data);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['accommodation', 'property-facilities', propertyId] });
      setModifiedRows((prev) => {
        const updated = { ...prev };
        delete updated[variables.facilityCode];
        return updated;
      });
      enqueueSnackbar('Facility updated successfully', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(`Error updating facility: ${error.message}`, { variant: 'error' });
    },
  });

  // Remove property facility mutation
  const removeMutation = useMutation({
    mutationFn: async (facilityCode: string) => {
      return await AccommodationService.removePropertyFacility({ propertyId, facilityCode });
    },
    onSuccess: (_data, facilityCode) => {
      queryClient.invalidateQueries({ queryKey: ['accommodation', 'property-facilities', propertyId] });
      setDeletingCodes((prev) => {
        const updated = new Set(prev);
        updated.delete(facilityCode);
        return updated;
      });
      enqueueSnackbar('Facility removed from property successfully', { variant: 'success' });
    },
    onError: (error: Error, facilityCode) => {
      setDeletingCodes((prev) => {
        const updated = new Set(prev);
        updated.delete(facilityCode);
        return updated;
      });
      enqueueSnackbar(`Error removing facility: ${error.message}`, { variant: 'error' });
    },
  });

  const handleAddNew = useCallback(() => {
    const tempCode = `${NEW_ROW_CODE_PREFIX}${Date.now()}`;
    const newRow: PropertyFacilityRowData = {
      code: tempCode,
      name: '',
      description: null,
      catalogGroup: undefined,
      icon: null,
      featured: false,
      displayOrder: undefined,
      isNew: true,
    };
    setLocalRows((prev) => [newRow, ...prev]);
    setModifiedRows((prev) => ({
      ...prev,
      [tempCode]: { facilityCode: '' },
    }));
  }, []);

  const handleSave = useCallback(
    (row: PropertyFacilityRowData) => {
      const changes = modifiedRowsRef.current[row.code];
      if (!changes) return;

      if (row.isNew || row.code.startsWith(NEW_ROW_CODE_PREFIX)) {
        // Add new facility to property
        if (!changes.facilityCode) {
          enqueueSnackbar('Please select a facility to add', { variant: 'error' });
          return;
        }
        addMutation.mutate({
          tempCode: row.code,
          facilityCode: changes.facilityCode,
          displayOrder: changes.displayOrder ?? undefined,
          featured: changes.featured ?? undefined,
        });
      } else {
        // Update existing property facility
        updateMutation.mutate({
          facilityCode: row.code,
          data: {
            displayOrder: changes.displayOrder,
            featured: changes.featured,
          },
        });
      }
    },
    [addMutation, updateMutation, enqueueSnackbar],
  );

  const handleClearChanges = useCallback((code: string) => {
    const isNewRow = code.startsWith(NEW_ROW_CODE_PREFIX);

    if (isNewRow) {
      setLocalRows((prev) => prev.filter((r) => r.code !== code));
    }

    setModifiedRows((prev) => {
      const updated = { ...prev };
      delete updated[code];
      return updated;
    });
  }, []);

  const handleDelete = useCallback(
    (code: string) => {
      const isNewRow = code.startsWith(NEW_ROW_CODE_PREFIX);

      if (isNewRow) {
        setLocalRows((prev) => prev.filter((r) => r.code !== code));
        setModifiedRows((prev) => {
          const updated = { ...prev };
          delete updated[code];
          return updated;
        });
      } else {
        setDeletingCodes((prev) => new Set(prev).add(code));
        removeMutation.mutate(code);
      }
    },
    [removeMutation],
  );

  // Process row updates from DataGrid cell editing
  const processRowUpdate = useCallback((newRow: PropertyFacilityRowData, _oldRow: PropertyFacilityRowData) => {
    // For this component, changes are tracked via direct state updates in the inline controls
    // This callback ensures DataGrid editing is properly configured but actual change tracking
    // happens in the onChange handlers of the inline edit controls
    return newRow;
  }, []);

  const columns: Array<GridColDef<PropertyFacilityRowData>> = useMemo(
    () => [
      {
        field: 'code',
        headerName: 'Facility',
        width: 300,
        editable: false,
        renderCell: (params: GridRenderCellParams<PropertyFacilityRowData>) => {
          const isNewRow = params.row.code.startsWith(NEW_ROW_CODE_PREFIX);
          if (isNewRow) {
            const selectedCode = modifiedRowsRef.current[params.row.code]?.facilityCode;
            const selectedFacility = allFacilities.find((f) => f.code === selectedCode);

            return (
              <Autocomplete
                size="small"
                options={availableFacilityOptions}
                value={selectedFacility ? { code: selectedFacility.code, name: selectedFacility.name } : null}
                onChange={(_event, newValue: FacilityOption | null) => {
                  setModifiedRows((prev) => ({
                    ...prev,
                    [params.row.code]: { ...prev[params.row.code], facilityCode: newValue?.code },
                  }));
                }}
                getOptionLabel={(option) => `${option.name} (${option.code})`}
                isOptionEqualToValue={(option, value) => option.code === value.code}
                renderInput={(inputParams) => (
                  <TextField {...inputParams} variant="outlined" placeholder="Select facility" />
                )}
                fullWidth
                sx={{ minWidth: 280 }}
              />
            );
          }
          return (
            <Typography variant="body2">
              {params.row.name} ({params.value})
            </Typography>
          );
        },
      },
      {
        field: 'catalogGroup',
        headerName: 'Group',
        width: 150,
        editable: false,
        renderCell: (params: GridRenderCellParams<PropertyFacilityRowData>) => (
          <Typography variant="body2">{params.value?.name ?? '-'}</Typography>
        ),
      },
      {
        field: 'displayOrder',
        headerName: 'Display Order',
        width: 120,
        editable: false,
        renderCell: (params: GridRenderCellParams<PropertyFacilityRowData>) => {
          const isNewRow = params.row.code.startsWith(NEW_ROW_CODE_PREFIX);
          const currentValue = isNewRow
            ? modifiedRowsRef.current[params.row.code]?.displayOrder
            : modifiedRowsRef.current[params.row.code]?.displayOrder ?? params.value;

          return (
            <NumberEditCell
              {...(params as any)}
              value={currentValue}
              onChange={(value) => {
                setModifiedRows((prev) => ({
                  ...prev,
                  [params.row.code]: { ...prev[params.row.code], displayOrder: value },
                }));
              }}
            />
          );
        },
      },
      {
        field: 'featured',
        headerName: 'Featured',
        width: 100,
        editable: false,
        renderCell: (params: GridRenderCellParams<PropertyFacilityRowData>) => {
          const isNewRow = params.row.code.startsWith(NEW_ROW_CODE_PREFIX);
          const currentValue = isNewRow
            ? modifiedRowsRef.current[params.row.code]?.featured ?? false
            : modifiedRowsRef.current[params.row.code]?.featured ?? params.value;

          return (
            <FeaturedEditCell
              {...(params as any)}
              value={currentValue}
              onChange={(value) => {
                setModifiedRows((prev) => ({
                  ...prev,
                  [params.row.code]: { ...prev[params.row.code], featured: value },
                }));
              }}
            />
          );
        },
      },
      {
        field: 'actions',
        headerName: 'Actions',
        width: 130,
        sortable: false,
        editable: false,
        renderCell: (params: GridRenderCellParams<PropertyFacilityRowData>) => {
          const hasChanges = Boolean(modifiedRowsRef.current[params.row.code]);
          const isSaving = addMutation.isPending || updateMutation.isPending;
          const isDeleting = deletingCodes.has(params.row.code);
          const isNewRow = params.row.code.startsWith(NEW_ROW_CODE_PREFIX);

          return (
            <Stack direction="row" spacing={0.5}>
              <Tooltip title={isNewRow ? 'Add facility' : 'Save changes'}>
                <span>
                  <IconButton
                    size="small"
                    onClick={() => handleSave(params.row)}
                    disabled={!hasChanges || isSaving || isDeleting}
                    sx={{ color: hasChanges && !isSaving ? blue[600] : undefined }}
                  >
                    {isSaving ? <CircularProgress size={18} /> : <SaveIcon />}
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={isNewRow ? 'Remove row' : 'Clear changes'}>
                <span>
                  <IconButton
                    size="small"
                    onClick={() => handleClearChanges(params.row.code)}
                    disabled={(!hasChanges && !isNewRow) || isSaving || isDeleting}
                  >
                    <ClearIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={isNewRow ? 'Remove row' : 'Remove facility'}>
                <span>
                  <IconButton
                    size="small"
                    onClick={() => handleDelete(params.row.code)}
                    disabled={isSaving || isDeleting}
                    sx={{ color: red[600] }}
                  >
                    {isDeleting ? <CircularProgress size={18} /> : <DeleteIcon />}
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          );
        },
      },
    ],
    [
      allFacilities,
      availableFacilityOptions,
      handleSave,
      handleClearChanges,
      handleDelete,
      addMutation.isPending,
      updateMutation.isPending,
      deletingCodes,
    ],
  );

  // Combine local new rows with API rows (local rows first to appear at top)
  const allRows = useMemo(() => {
    return [...localRows, ...propertyFacilities];
  }, [localRows, propertyFacilities]);

  if (error) {
    return (
      <Alert severity="error">
        Error loading property facilities: {error instanceof Error ? error.message : 'Unknown error'}
      </Alert>
    );
  }

  return (
    <Stack direction="column" spacing={2}>
      <Stack direction="row" spacing={2} alignItems="center">
        <Typography variant="h6">Property Facilities</Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddNew}>
          Add Facility
        </Button>
      </Stack>

      <Typography variant="body2" color="textSecondary">
        Manage facilities associated with this property. Click Add Facility to associate a new facility, or modify
        Display Order and Featured status for existing facilities.
      </Typography>

      <Table
        columns={columns}
        rows={allRows}
        getRowId={(row) => row.code}
        paginationMode="client"
        isLoading={isLoading}
        autoHeight
        dataGridProps={{
          apiRef,
          editMode: 'cell',
          processRowUpdate,
          onProcessRowUpdateError: (updateError) => {
            enqueueSnackbar(`Error processing row update: ${updateError.message}`, { variant: 'error' });
          },
          getRowHeight: () => 'auto',
          sx: {
            '& .MuiDataGrid-cell': {
              py: 1,
            },
          },
        }}
      />
    </Stack>
  );
}
